import { useMemo } from "react";
import { subDays, startOfDay, endOfDay } from "date-fns";
import {
  useGetDisposalSiteAnalyticsQuery,
  useGetGovDashboardAnalyticsQuery,
} from "~/shared/api/govDash/govDash.api";

export interface UseMonitorStatsParams {
  districtOrganizationIds: bigint[];
  serviceProviderIds: bigint[];
  selectedDate: Date;
}

export interface MonitorStats {
  wasteTons: number;
  wasteTonsTrendPercent: number;
  anomaliesDetected: number;
  anomaliesTrendPercent: number;
}

const toFromTo = (date: Date) => ({
  from: { seconds: BigInt(Math.floor(startOfDay(date).getTime() / 1000)) },
  to: { seconds: BigInt(Math.floor(endOfDay(date).getTime() / 1000)) },
});

const trendPercent = (today: number, yesterday: number): number =>
  yesterday > 0 ? Number((((today - yesterday) / yesterday) * 100).toFixed(1)) : 0;

/** Real counts backing the Monitor page's stat cards, scoped to the same day + org filters as the map, plus day-over-day trend vs the prior day. */
export const useMonitorStats = ({
  districtOrganizationIds,
  serviceProviderIds,
  selectedDate,
}: UseMonitorStatsParams): MonitorStats => {
  const ownerOrganizationId = serviceProviderIds[0];

  const todayFromTo = useMemo(() => toFromTo(selectedDate), [selectedDate]);
  const yesterdayFromTo = useMemo(() => toFromTo(subDays(selectedDate, 1)), [selectedDate]);

  const { data: scaleAnalyticsToday } = useGetDisposalSiteAnalyticsQuery(
    {
      ownerOrganizationId,
      fromTo: todayFromTo,
    },
    { skip: ownerOrganizationId === undefined },
  );
  const { data: scaleAnalyticsYesterday } = useGetDisposalSiteAnalyticsQuery(
    {
      ownerOrganizationId,
      fromTo: yesterdayFromTo,
    },
    { skip: ownerOrganizationId === undefined },
  );

  const { data: dashboardAnalyticsToday } = useGetGovDashboardAnalyticsQuery({
    filter: { districtOrganizationIds, serviceProviderIds },
    fromTo: todayFromTo,
  });
  const { data: dashboardAnalyticsYesterday } = useGetGovDashboardAnalyticsQuery({
    filter: { districtOrganizationIds, serviceProviderIds },
    fromTo: yesterdayFromTo,
  });

  return useMemo(() => {
    const wasteTons = Number(scaleAnalyticsToday?.totalNettoKg ?? 0) / 1000;
    const wasteTonsYesterday = Number(scaleAnalyticsYesterday?.totalNettoKg ?? 0) / 1000;

    const anomaliesDetected = Number(dashboardAnalyticsToday?.totalAnomalies ?? 0);
    const anomaliesYesterday = Number(dashboardAnalyticsYesterday?.totalAnomalies ?? 0);

    return {
      wasteTons,
      wasteTonsTrendPercent: trendPercent(wasteTons, wasteTonsYesterday),
      anomaliesDetected,
      anomaliesTrendPercent: trendPercent(anomaliesDetected, anomaliesYesterday),
    };
  }, [scaleAnalyticsToday, scaleAnalyticsYesterday, dashboardAnalyticsToday, dashboardAnalyticsYesterday]);
};
